import { oceanBillingStateForRegion } from '@/data/oceanpayment-billing-states.js'

/**
 * 使用 ipinfo.io 根据访问者公网 IP 获取大致地理位置（城市级，非 GPS）。
 * 文档：https://ipinfo.io/developers
 * 可选：在项目根目录 .env 中设置 VUE_APP_IPINFO_TOKEN 以提高免费额度与稳定性。
 *
 * billing_state：与 Oceanpayment 附录一致，查 src/data/oceanpayment-state-appendix.json。
 */
const STORAGE_KEY = 'dailyshort_ipinfo_v1'

function buildUrl() {
  const token = process.env.VUE_APP_IPINFO_TOKEN
  if (token) {
    return `https://ipinfo.io/json?token=${encodeURIComponent(token)}`
  }
  return 'https://ipinfo.io/json'
}

/**
 * @returns {Promise<{
 *   ip: string,
 *   hostname?: string,
 *   city?: string,
 *   region?: string,
 *   country?: string,
 *   loc?: string,
 *   org?: string,
 *   postal?: string,
 *   timezone?: string
 * }>}
 */
export async function getClientIpInfo({ useSessionCache = true } = {}) {
  if (useSessionCache && typeof sessionStorage !== 'undefined') {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw) {
      try {
        return JSON.parse(raw)
      } catch (_) {
        sessionStorage.removeItem(STORAGE_KEY)
      }
    }
  }

  const res = await fetch(buildUrl(), {
    credentials: 'omit',
    headers: { Accept: 'application/json' }
  })
  if (!res.ok) {
    throw new Error(`ipinfo HTTP ${res.status}`)
  }
  const data = await res.json()
  if (useSessionCache && typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }
  return data
}

/**
 * 将 ipinfo 响应拼成可读地址（country 为两位国家码）。
 * @param {Record<string, string | undefined> | null | undefined} info
 */
export function formatIpInfoLine(info) {
  if (!info) return ''
  const parts = [info.city, info.region, info.country].filter(Boolean)
  return parts.join(', ')
}

/**
 * 从 ipinfo 推导 Oceanpayment billing_state（值与附录「州代码」列一致，未必含国家前缀）。
 * - 付费 geo：优先用 geo.region 英文地名查附录；否则用 geo.region_code（附录中的 subdivision 码）。
 * - 免费 /json：country + region 英文地名查附录。
 *
 * @param {Record<string, unknown> | null | undefined} info getClientIpInfo 返回值
 * @returns {string}
 */
export function billingStateFromIpInfo(info) {
  if (!info || typeof info !== 'object') return ''

  const geo = info.geo
  if (geo && typeof geo === 'object') {
    const cc = String(geo.country_code || '').toUpperCase()
    const rn = typeof geo.region === 'string' ? geo.region.trim() : ''
    if (cc && rn) {
      const o = oceanBillingStateForRegion(cc, rn)
      if (o) return o
    }
    const rc =
      geo.region_code !== undefined && geo.region_code !== null && geo.region_code !== ''
        ? String(geo.region_code).toUpperCase()
        : ''
    if (cc && rc) return rc
  }

  const country = typeof info.country === 'string' ? info.country.toUpperCase() : ''
  const region = typeof info.region === 'string' ? info.region.trim() : ''

  if (!country) return ''

  const fromOcean = oceanBillingStateForRegion(country, region)
  if (fromOcean) return fromOcean

  return ''
}
